/**
 * Coach-local exercise favorites + ratings for faster Live Training planning.
 * Persisted in localStorage until a server-backed catalog preference API exists.
 */

const STORAGE_KEY = "smarttt_coach_exercise_prefs_v1";

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota / private mode */
  }
}

export function getExercisePref(exId) {
  if (exId == null) return { favorite: false, rating: 0 };
  const row = readStore()[String(exId)];
  return {
    favorite: Boolean(row?.favorite),
    rating: Math.max(0, Math.min(5, Number(row?.rating) || 0)),
  };
}

export function setExerciseFavorite(exId, favorite) {
  if (exId == null) return getExercisePref(exId);
  const store = readStore();
  const key = String(exId);
  const prev = store[key] || {};
  const next = {
    ...prev,
    favorite: Boolean(favorite),
    updatedAt: Date.now(),
  };
  if (!next.favorite && !(Number(next.rating) > 0)) {
    delete store[key];
  } else {
    store[key] = next;
  }
  writeStore(store);
  return getExercisePref(exId);
}

export function setExerciseRating(exId, rating) {
  if (exId == null) return getExercisePref(exId);
  const store = readStore();
  const key = String(exId);
  const prev = store[key] || {};
  const clamped = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  const next = {
    ...prev,
    rating: clamped,
    updatedAt: Date.now(),
  };
  if (!next.favorite && !(next.rating > 0)) {
    delete store[key];
  } else {
    store[key] = next;
  }
  writeStore(store);
  return getExercisePref(exId);
}

export function toggleExerciseFavorite(exId) {
  const cur = getExercisePref(exId);
  return setExerciseFavorite(exId, !cur.favorite);
}

/**
 * Favorites first, then higher rating, then original order.
 */
export function sortExercisesByCoachPref(list) {
  if (!Array.isArray(list) || list.length < 2) return list || [];
  const store = readStore();
  return list
    .map((row, index) => {
      const pref = store[String(row?.ex_id)] || {};
      return {
        row,
        index,
        favorite: Boolean(pref.favorite),
        rating: Math.max(0, Math.min(5, Number(pref.rating) || 0)),
      };
    })
    .sort((a, b) => {
      if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
      if (a.rating !== b.rating) return b.rating - a.rating;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}
